import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../contexts/UserContext';

/**
 * TrialBanner
 * 
 * Mostra banner de contagem regressiva quando:
 * - Usuário está em trial
 * - Plano expira em menos de 7 dias
 * - Plano está próximo dos limites
 * 
 * Super Admin nunca vê este banner.
 */
export default function TrialBanner() {
  const navigate = useNavigate();
  const { subscription, isSuperAdmin, loading } = useUserProfile();
  const [dismissed, setDismissed] = useState(false);

  if (loading || isSuperAdmin || dismissed) return null;
  if (!subscription) return null;

  // Calcular dias restantes
  const expiraEm = subscription.expiraEm?.toDate?.()
    || new Date(subscription.expiraEm?.seconds ? subscription.expiraEm.seconds * 1000 : subscription.expiraEm);
  const now = new Date();
  const diff = expiraEm - now;
  const diasRestantes = Math.max(0, Math.ceil(diff / 86400000));

  // Só mostrar banner quando é trial OU plano expira em ≤ 7 dias
  const isTrial = subscription.planoId === 'trial' || subscription.status === 'trial';
  const expirandoEmBreve = diasRestantes <= 7 && diasRestantes > 0;

  if (!isTrial && !expirandoEmBreve) return null;

  // Cores baseadas na urgência
  let bgColor, textColor, borderColor, icon;
  if (diasRestantes <= 1) {
    bgColor = 'rgba(239,68,68,0.06)';
    textColor = '#DC2626';
    borderColor = 'rgba(239,68,68,0.15)';
    icon = '🚨';
  } else if (diasRestantes <= 3) {
    bgColor = 'rgba(245,158,11,0.06)';
    textColor = '#D97706';
    borderColor = 'rgba(245,158,11,0.15)';
    icon = '⚠️';
  } else {
    bgColor = 'rgba(59,130,246,0.06)';
    textColor = '#3B82F6';
    borderColor = 'rgba(59,130,246,0.15)';
    icon = '⏱️';
  }

  const mensagem = isTrial
    ? diasRestantes === 0
      ? 'Seu teste grátis expira hoje!'
      : diasRestantes === 1
        ? 'Seu teste grátis expira amanhã!'
        : `Seu teste grátis expira em ${diasRestantes} dias.`
    : diasRestantes === 0
      ? 'Seu plano expira hoje!'
      : diasRestantes === 1
        ? 'Seu plano expira amanhã!'
        : `Seu plano expira em ${diasRestantes} dias.`;

  return (
    <div
      className="trial-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        marginBottom: 16,
        fontFamily: 'var(--font)',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{icon}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: textColor }}>
          {mensagem}
        </span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 6 }}>
          {isTrial
            ? 'Escolha um plano para continuar usando o SimulaBank.'
            : 'Renove seu plano para manter o acesso.'
          }
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={() => navigate('/planos')}
          style={{
            padding: '6px 16px', borderRadius: 8,
            background: textColor, color: '#fff',
            border: 'none', fontFamily: 'var(--font)',
            fontSize: '0.75rem', fontWeight: 700,
            cursor: 'pointer', transition: 'opacity 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {isTrial ? 'Ver Planos' : 'Renovar'}
        </button>

        <button
          onClick={() => setDismissed(true)}
          style={{
            width: 28, height: 28, borderRadius: 6,
            background: 'transparent', border: `1px solid ${borderColor}`,
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: '0.72rem', color: 'var(--text-muted)',
            flexShrink: 0,
          }}
          title="Dispensar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
